import { memo, type ReactNode, useCallback, useLayoutEffect } from "react";
import { SearchDisplayMode } from "../../models";
import SearchResultList from "~/Search/components/ProductResults/views/ListView/SearchResultList";
import Pagination from "../common/Pagination";
import { NoSearchResults } from "../common/NoSearchResults";
import { useDispatch, useSelector } from "react-redux";
import { selectIsInFlight, selectIsInit, selectProductResults } from "../../redux/searchSelectors";
import { type RootState } from "../../../redux/store";
import { clearAll, clearAllTechFilters } from "../../redux/actions";
import NotFoundContactUsBar from "~/Search/components/common/NotFoundContactUsBar/NotFoundContactUsBar";
import DisclaimerCountryOrigin from "../../../common/components/Messages/DisclaimerCountryOrigin";
import { ReduxProductTabularView } from "~/Search/components/ProductResults/views/TabularView/ReduxProductTabularView";
import { useResetQualtrics } from "../../../common/hooks/useResetQualtrics";
import { useReduxUnitMode } from "~/feature/UnitMode/useReduxUnitMode";
import { SeriesTreeView } from "~/Search/components/ProductResults/views/SeriesTreeView/SeriesTreeView";
import { SearchPageType } from "../../useSearchPageType";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { type PdpPageProductData } from "~/ProductDetails/models/PdpPageProductData";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import { useTranslation } from "react-i18next";
import { useSearchPageType } from "~/Search/useSearchPageType";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { getInitialResultsViewMode } from "~/Search/utils/searchUtils";
import classes from "./ProductResults.module.less";
import { Alert } from "~/common/m3/Alert/Alert";
import { Stack } from "@mui/material";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { useSelectAppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import Spinner from "~/common/components/Spinner/Spinner";
import { TopFilterBar } from "./components/TopFilterBar";
import { SkeletonResultList } from "./views/ListView/components/SkeletonResultList";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useSearchPagination } from "./useSearchPagination";
import { selectIsUseNewDesign, selectSearchDisplayMode } from "~/feature/UserPreferences/userPreferencesSelectors";
import { changeDisplayModeThunk } from "~/feature/UserPreferences/userPreferencesSlice";
import { L1CategoryNavigation } from "../L1CategoryNavigation";
import { NewDesignReduxProductTabularView } from "./views/NewTabularView/NewTabularView";
import { NewTabularViewSkeleton } from "./views/NewTabularView/NewTabularViewSkeleton";

interface Props {
  recommendationPosition?: number;
  recommendationComponent?: ReactNode;
}

const ProductResults = ({ recommendationPosition, recommendationComponent }: Props) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const dispatch = useDispatch();
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const results = useSelector(selectProductResults);
  const appliedFilters = useSelectAppliedFilters();

  const pageType = useSearchPageType();
  // TODO - other cases! Campaign page?
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";

  const inFlight = useSelector(selectIsInFlight);
  const isInit = useSelector(selectIsInit);

  let displayMode = useSelector(selectSearchDisplayMode);
  const useNewDesign = useSelector(selectIsUseNewDesign);
  const setDisplayMode = useCallback((mode: SearchDisplayMode) => dispatch(changeDisplayModeThunk(mode)), []);

  if (displayMode === SearchDisplayMode.TABULAR && useNewDesign && screenSize < useNumericScreenSize.desktopM) {
    displayMode = SearchDisplayMode.LIST;
  }

  const productCodes = useSelector((state: RootState) => state.srp.results.results.map((row) => row.productCode));

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const hasSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
  );

  const tariffSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
  );

  useLayoutEffect(() => {
    if (isInit && !isProductFamilySelected) {
      setDisplayMode(getInitialResultsViewMode());
    }
  }, [isInit, isProductFamilySelected]);

  const { pageAmount, currentPage, onPageChanged } = useSearchPagination();

  useResetQualtrics([inFlight]);

  const { value: unitMode, onChange: setUnitMode } = useReduxUnitMode(gaInteractionType, () =>
    dispatch(clearAllTechFilters()),
  );

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const tabularViewConfigured = isProductFamilySelected || pageType === SearchPageType.PLP;
  const showChildCategoryNavigation = !isProductFamilySelected && pageType == SearchPageType.SEARCH;

  const showSkeletons =
    displayMode === SearchDisplayMode.LIST || (useNewDesign && displayMode === SearchDisplayMode.TABULAR);

  const canShowNewDesignTabular = screenSize >= useNumericScreenSize.desktopM;

  let paginationComponent = null;
  if (pageAmount > 1) {
    paginationComponent = (
      <Pagination
        classes={{ root: "scha__srp__pagination" }}
        pageAmount={pageAmount}
        currentPage={currentPage}
        onPageChange={onPageChanged}
        renderHrefs={true}
        goToIdAfterChange={"#resultsHeader"}
      />
    );
  }
  if (displayMode === SearchDisplayMode.TABULAR && useNewDesign) {
    // Pagination handled by the new tabular view
    paginationComponent = null;
  }

  if (!isInit || results.length) {
    return (
      <Stack
        id={"productResults"}
        sx={{
          gap: { phone: 2, "desktop-s": 3 },
        }}
      >
        {isInit && (
          <TopFilterBar
            unitMode={unitMode}
            displayMode={displayMode}
            setUnitMode={setUnitMode}
            setDisplayMode={setDisplayMode}
            newDesign={useNewDesign}
            canShowNewDesignTabular={canShowNewDesignTabular}
          />
        )}

        {showChildCategoryNavigation && <L1CategoryNavigation />}

        {inFlight && !showSkeletons && <Spinner className="spinner--result-list" />}

        {(!inFlight || showSkeletons) && (
          <>
            {displayMode === SearchDisplayMode.TABULAR && (
              <>
                {useNewDesign && inFlight && (
                  <div className={classes.tabularContainer}>
                    <NewTabularViewSkeleton />
                  </div>
                )}
                {useNewDesign && !inFlight && (
                  <div className={classes.tabularContainer}>
                    {tabularViewConfigured && <NewDesignReduxProductTabularView />}
                    {!tabularViewConfigured && (
                      <Alert severity={"info"} className={"scha__srp__select-category-msg"}>
                        {t("page.srp.selectCategory")}
                      </Alert>
                    )}
                  </div>
                )}
                {!useNewDesign && (
                  <div className={classes.tabularContainer}>
                    {recommendationComponent}
                    {tabularViewConfigured && (
                      <>
                        <ReduxProductTabularView />
                        {paginationComponent}
                      </>
                    )}
                    {!tabularViewConfigured && (
                      <Alert severity={"info"} className={"scha__srp__select-category-msg"}>
                        {t("page.srp.selectCategory")}
                      </Alert>
                    )}
                  </div>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.LIST && (
              <>
                {inFlight ? (
                  <SkeletonResultList
                    amount={20}
                    recommendationPosition={recommendationPosition}
                    recommendationComponent={recommendationComponent}
                  />
                ) : (
                  <>
                    <SearchResultList
                      results={results}
                      recommendationPosition={recommendationPosition}
                      recommendationComponent={recommendationComponent}
                    />
                    {paginationComponent}
                  </>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.SERIES_TREE && <SeriesTreeView />}

            {!inFlight && (
              <>
                <NotFoundContactUsBar className="scha__srp__not-found-disc mb-2" />
                <div className="scha__srp__disclaimers">
                  <Alert severity={"info"} className={"scha__srp__action-bar__imperial-msg"}>
                    {t("page.srp.unitTypeMessage")}
                  </Alert>
                  <DisclaimerCountryOrigin />
                </div>
                <IncludesSurchargeDisclaimer
                  hasSurcharge={hasSurcharge}
                  tariffSurcharge={tariffSurcharge}
                  className={"mt-30"}
                />
              </>
            )}
          </>
        )}

        <button hidden type="submit" />
      </Stack>
    );
  }

  return (
    <div id={"productResults"}>
      <NoSearchResults variant={appliedFilters.length === 0 ? "search" : "filter"} onClearAll={handleClearAll} />
      <button hidden type="submit" />
    </div>
  );
};

export const selectCurrentPage = (state: RootState) => state.srp.searchParams.currentPage;
const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;

export default memo(ProductResults);
